import React from "react";

const ParameterBehavior: React.FC = () => {
  // Parameter data array for sorting
  const parameters = [
    {
      name: "type",
      type: "string",
      description: `Type of slide as defined below:

- TextandImage: Relevant for slides where an overview along with an image needs to be presented.

Examples - overviews and descriptions.

- Points: Relevant for slides which are content-heavy and need to showcase information in bullet points. Examples - itemized lists, categories and different aspects.

- Phases: Relevant for slides where information needs to be depicted chronologically. Examples - timelines and roadmaps.

- Images: Relevant for slides with only images. Examples - screenshots and product images.

- Tables: Relevant for slides for tabular representation. Examples - comparisons and data analysis.

- People: Relevant for slides to show specific people. Examples - team and testimonials.

- Graphs: Relevant for slides to showcase graphical representation of data. Examples - financial growth and user distribution.

- Statistics: Relevant for slides to display data points. Examples - metrics and traction.`,
      defaultValue: "-",
    },
    {
      name: "websiteUrl",
      type: "string",
      description:
        "A URL to fetch context/data from. This acts as a supporting data for the presentation content.",
      defaultValue: "-",
    },
    {
      name: "pdfLink",
      type: "string",
      description:
        "A public link to a PDF which needs to be converted to a presentation.",
      defaultValue: "-",
    },
    {
      name: "presentationID",
      type: "string",
      description: `Google Slides presentation ID (if slide needs to be added to an existing presentation).

This is the ID after "d/" in the Google Slides URL

Example:
Google Slides URL: https://docs.google.com/presentation/d/17lyk_7iW7dXrtafb7df128QfIAjwvjTXInHURIhP0us/

Presentation ID: 17lyk_7iW7dXrtafb7df128QfIAjwvjTXInHURIhP0us`,
      defaultValue: "-",
    },
    {
      name: "outline",
      type: "array",
      description:
        "An array of slides to be generated. Each slide comprises of title, type, and keywords.",
      defaultValue: "-",
    },
    {
      name: "pptType",
      type: "string",
      description: "The type of presentation to be generated in 2-3 words.",
      defaultValue: "-",
    },
    {
      name: "keywords",
      type: "string",
      description:
        "The keywords relevant to the slide title to help with content generation.",
      defaultValue: "-",
    },
    {
      name: "theme",
      type: "string",
      description: `The theme defines the background colour of the slides. All themes use the branding colours. Available options:
Light
Dark
Branded`,
      defaultValue: "Light",
    },
    {
      name: "title",
      type: "string",
      description: "Title of the slide.",
      defaultValue: "-",
    },
    {
      name: "colors",
      type: "object",
      description:
        "Primary and secondary brand colors for the presentation. The system creates a relevant color palette based on these colours.",
      defaultValue: "Primary: #17191A\nSecondary: #7D9FD0",
    },
    {
      name: "creativity",
      type: "number",
      description:
        "Content creativity level between 1 (most structured) and 10 (most creative).",
      defaultValue: "6",
    },
    {
      name: "data",
      type: "object",
      description:
        "Slide content data structure (varies by slide type as per the request body below).",
      defaultValue: "-",
    },
    {
      name: "documentName",
      type: "string",
      description: "Name of the presentation.",
      defaultValue: "-",
    },
    {
      name: "input",
      type: "string",
      description:
        "User narrative or prompt describing what the slide should contain.",
      defaultValue: "-",
    },
    {
      name: "language",
      type: "string",
      description: `Target language for the presentation. Available options:
English
Chinese (Simplified)
Chinese (Traditional)
Spanish
French
German
Portuguese (Brazilian)
Portuguese (European)
Italian
Russian
Japanese
Korean
Dutch
Arabic (Modern Standard)
Polish
Turkish
Vietnamese`,
      defaultValue: "English",
    },
    {
      name: "pptInput",
      type: "string",
      description:
        "Input text and instructions relevant to the required presentation.",
      defaultValue: "-",
    },
  ];

  // Sort parameters alphabetically by name
  const sortedParameters = [...parameters].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  return (
    <section
      id="parameter-behavior"
      className="mt-12 pt-8 border-t border-slate-200 scroll-mt-20"
    >
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
        Parameter Behavior
      </h2>
      <p className="mt-4 text-slate-600">
        This section explains the behavior of parameters shared across{" "}
        <strong>Generate Custom Slide</strong> and{" "}
        <strong>Generate Slide from Narrative</strong> endpoints. Understanding
        these behaviors will help you use the API more effectively.
      </p>

      <div id="parameter-library" className="mt-8 scroll-mt-20">
        <h3 className="text-xl font-semibold text-slate-800 mb-4">
          Parameter Library
        </h3>
        <div className="w-full border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full table-auto divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="w-1/2 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Default Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {sortedParameters.map((param, index) => (
                <tr key={param.name}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-700">
                    {param.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {param.type}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {param.name === "presentationID" ? (
                      <>
                        Google Slides presentation ID (if slide needs to be
                        added to an existing presentation).
                        <br />
                        <br />
                        This is the ID after "d/" in the Google Slides URL
                        <br />
                        <br />
                        Example:
                        <br />
                        Google Slides URL:{" "}
                        <a
                          href="https://docs.google.com/presentation/d/17lyk_7iW7dXrtafb7df128QfIAjwvjTXInHURIhP0us/"
                          className="text-blue-600 hover:underline"
                        >
                          https://docs.google.com/presentation/d/17lyk_7iW7dXrtafb7df128QfIAjwvjTXInHURIhP0us/
                        </a>
                        <br />
                        <br />
                        Presentation ID:
                        17lyk_7iW7dXrtafb7df128QfIAjwvjTXInHURIhP0us
                      </>
                    ) : (
                      param.description.split("\n").map((line, lineIndex) => (
                        <span key={lineIndex}>
                          {line}
                          {lineIndex <
                            param.description.split("\n").length - 1 && <br />}
                        </span>
                      ))
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {param.defaultValue.split("\n").map((line, lineIndex) => (
                      <span key={lineIndex}>
                        {line}
                        {lineIndex <
                          param.defaultValue.split("\n").length - 1 && <br />}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Presentation ID Behavior Subsection */}
      <div id="presentation-id-behavior" className="mt-10 scroll-mt-20">
        <h3 className="text-xl font-semibold text-slate-800 mb-4">
          Presentation ID Behavior
        </h3>
        <p className="text-slate-600 mb-6">
          The behavior of the{" "}
          <code className="bg-slate-100 px-1 py-0.5 rounded text-sm">
            presentationID
          </code>{" "}
          parameter depends on whether it is included in your API request.
        </p>

        <div className="space-y-6">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-md">
            <h4 className="text-lg font-semibold text-blue-900 mb-2">
              1. If presentationID is not provided:
            </h4>
            <ul className="text-blue-800 space-y-2">
              <li>
                • A new presentation will be automatically created by Zynth
              </li>
              <li>
                • Only one slide will be generated using the provided input
                (e.g., slide content, data, etc.)
              </li>
              <li>
                • This is ideal for quickly generating a standalone slide or
                testing output without managing a full deck
              </li>
              {/* <li>• You will receive a new presentationID in the API response that you can use for future requests</li> */}
            </ul>
          </div>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-md">
            <h4 className="text-lg font-semibold text-green-900 mb-2">
              2. If presentationID is provided:
            </h4>
            <ul className="text-green-800 space-y-2">
              <li>• The slide will be added to an existing presentation</li>
              <li>
                • This allows you to build multi-slide presentations by making
                multiple API calls
              </li>
              <li>
                • The presentationID must meet one of the following conditions:
              </li>
            </ul>

            <div className="mt-4 ml-4 space-y-3">
              <div className="bg-white border border-green-200 p-3 rounded">
                <h5 className="font-medium text-green-900">
                  ✅ Valid presentationID sources:
                </h5>
                <ul className="text-green-700 mt-2 space-y-1 text-sm">
                  <li>
                    • <strong>Previously generated using Zynth API:</strong> You
                    received it in a past API response
                  </li>
                  <li>
                    • <strong>Manually created and authorized:</strong> Created
                    by your team and shared with Zynth team by providing edit
                    access to{" "}
                    <a
                      href="mailto:team@zynth.ai"
                      className="text-blue-600 hover:underline"
                    >
                      team@zynth.ai
                    </a>{" "}
                    for validation and linking
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-md">
            <h4 className="text-lg font-semibold text-red-900 mb-2">
              ⚠️ Important Notes:
            </h4>
            <ul className="text-red-800 space-y-2">
              <li>
                • Using an invalid or unauthorized presentationID will result in
                an error or rejection of the request
              </li>
              {/* <li>• If you have a Google Slides presentation that wasn't created through Zynth, contact our support team to authorize it</li>
              <li>• Always store the presentationID from successful API responses for future use</li> */}
            </ul>
          </div>
        </div>

        <div className="mt-6 bg-slate-50 p-4 rounded-md">
          <h4 className="text-md font-semibold text-slate-800 mb-2">
            Example Workflow:
          </h4>
          <ol className="text-slate-700 space-y-2 text-sm">
            <li>
              <strong>1.</strong> First API call without presentationID →
              Creates new presentation, returns presentationID
            </li>
            <li>
              <strong>2.</strong> Subsequent API calls with the returned
              presentationID → Adds slides to existing presentation
            </li>
            <li>
              <strong>3.</strong> Build complete multi-slide presentation
              through multiple API calls
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
};

export default ParameterBehavior;
